import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { fcErr } from "@/pages/Faceless";
import { Field, inputCls } from "@/components/auth/LoginForm";

const PLATFORMS = ["YouTube", "YouTube Shorts", "Instagram Reels", "Facebook Reels"];
const LANGS = ["Hindi", "English", "Hinglish", "Tamil", "Telugu", "Marathi", "Bengali", "Gujarati", "Kannada", "Malayalam", "Punjabi"];
const FORMATS = ["Long-form explainer (8-12 min)", "Listicle (5-8 min)", "Story / documentary (10-15 min)", "Shorts / Reels (30-60s)", "News breakdown (4-6 min)"];
const MONEY = ["AdSense only", "AdSense + affiliate", "Own digital product", "Service / consulting", "Newsletter / community", "Lead magnet", "No monetization yet"];
const SELECTS = { platform: PLATFORMS, language: LANGS, format: FORMATS, monetization: MONEY };
const TEXT = [["name", "Channel name"], ["niche", "Niche / topic"], ["audience", "Target audience"], ["country", "Country"], ["frequency", "Posting frequency"], ["existing_url", "Existing channel URL"], ["tagline", "Tagline"], ["positioning", "Positioning"], ["voice_personality", "Voice / personality"], ["content_pillars", "Content pillars (comma separated)"]];

export function ChannelEditor({ channel, onSaved, onDeleted, onClose }) {
  const st = channel.strategy || {};
  const [f, setF] = useState({
    name: channel.name || "", niche: channel.niche || "", audience: channel.audience || "", country: channel.country || "", language: channel.language || "Hindi",
    platform: channel.platform || "YouTube", format: channel.format || FORMATS[0], frequency: channel.frequency || "", monetization: channel.monetization || MONEY[1],
    existing_url: channel.existing_url || "", tagline: st.channel_identity?.tagline || "", positioning: st.positioning || "", voice_personality: st.voice_personality || "",
    content_pillars: (st.content_pillars || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, existing_url: f.existing_url || null, content_pillars: f.content_pillars.split(",").map((s) => s.trim()).filter(Boolean) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" ) delete payload[k]; });
      const r = await api.patch(`/faceless/channels/${channel.channel_id}`, payload);
      toast.success("Channel updated"); onSaved(r.data);
    } catch (e) { toast.error(fcErr(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${channel.name}" and all its ideas, videos, offers and calendar? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/faceless/channels/${channel.channel_id}`); toast.success("Channel deleted"); onDeleted(); }
    catch (e) { toast.error(fcErr(e)); setBusy(false); }
  };

  return (
    <div data-testid="fc-channel-editor" className="glass rounded-2xl p-5 mb-4 relative">
      <button data-testid="fc-editor-close" onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X size={16} /></button>
      <div className="font-display font-semibold mb-3">Edit channel · <span className="text-gradient">{channel.name}</span></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TEXT.slice(0, 6).map(([k, l]) => <Field key={k} label={l}><input data-testid={`fc-edit-${k}`} value={f[k]} onChange={set(k)} className={inputCls} /></Field>)}
        {Object.entries(SELECTS).map(([k, opts]) => (
          <Field key={k} label={k[0].toUpperCase() + k.slice(1)}>
            <select data-testid={`fc-edit-${k}`} value={f[k]} onChange={set(k)} className={inputCls}>{opts.map((o) => <option key={o} className="bg-[#0E1019]">{o}</option>)}</select>
          </Field>
        ))}
        {TEXT.slice(6).map(([k, l]) => <Field key={k} label={l}><input data-testid={`fc-edit-${k}`} value={f[k]} onChange={set(k)} className={inputCls} /></Field>)}
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button data-testid="fc-edit-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-50">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save changes</button>
        <button data-testid="fc-edit-delete" onClick={del} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50"><Trash2 size={12} /> Delete channel</button>
      </div>
    </div>
  );
}
